import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/joy";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { ProgramContentType } from "data/BlockComponent";
import { getFilledCells, resolveAutoFill } from "logic/autoFill";
import { buildUserSimRequest } from "logic/userSimRequest";
import { getTaskIdFromTaskKey } from "logic/logics";
import useAutoFillStream, {
  INITIAL_AUTO_FILL_STATE,
} from "hooks/useAutoFillStream";
import useAutoFillRuns from "hooks/useAutoFillRuns";
import useIsInappWebview from "hooks/useIsInappWebview";
import { getLatestTranslationVersion } from "hooks/useGetBlankTaskData";
import { userAtom } from "recoil/userAtom";
import { patientAtom } from "recoil/patientAtom";
import { taskDataAtom } from "recoil/taskDataAtom";
import { autoFilledCellAtom } from "recoil/autoFilledCellAtom";
import {
  autoFillDockOpenAtom,
  autoFillRunSelector,
  autoFillViewingAtom,
  getAutoFillRunKey,
} from "recoil/autoFillRunAtom";
import CustomModal from "components/common/CustomModal";
import AutoFillProgress from "./AutoFillProgress";
import AutoFillRunningButton from "./AutoFillRunningButton";

// 교육용 모의 내담자 계정 전용 — user_sim Agent 로 활동지를 마치기 직전 상태까지 채운다.
// 앱: 진행 팝업으로 화면을 막고, 활동지를 나가면 멈춘다 (활동지마다 새 WebView).
// 웹: 전역에서 돌아 다른 활동지로 가도 계속 채운다 — 진행은 오른쪽 아래(AutoFillDock), 이 활동지는 잠근다(TaskContent)
export default function AutoFillButton({
  taskKey,
  thoughtRecordKey,
  title,
  translationVersion,
  contentData,
  setContentData,
  setMoveToIndex,
}: {
  taskKey: string;
  thoughtRecordKey?: string;
  title: string;
  translationVersion?: string;
  contentData: ProgramContentType[];
  setContentData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  setMoveToIndex?: React.Dispatch<React.SetStateAction<number | undefined>>;
}) {
  const isInappWebview = useIsInappWebview();
  // 앱 — 화면 상태
  const stream = useAutoFillStream();
  // 요청을 만든 시점의 내용 (앱) — 웹은 실행에 들고 있다
  const [baseContent, setBaseContent] = useState<ProgramContentType[]>();
  // 웹 — 전역 상태
  const runKey = getAutoFillRunKey(taskKey, thoughtRecordKey);
  const globalRun = useRecoilValue(autoFillRunSelector(runKey));
  const runs = useAutoFillRuns();
  const setDockOpen = useSetRecoilState(autoFillDockOpenAtom);
  const setViewing = useSetRecoilState(autoFillViewingAtom);

  const run = isInappWebview ? stream : (globalRun ?? INITIAL_AUTO_FILL_STATE);
  const runBaseContent = isInappWebview ? baseContent : globalRun?.baseContent;
  const { phase, request, values, error } = run;

  // Cell.tsx 와 같은 번역 범위
  const { t } = useTranslation("translation", {
    keyPrefix: `task.activity.${getTaskIdFromTaskKey(taskKey)}.${
      translationVersion ??
      getLatestTranslationVersion(getTaskIdFromTaskKey(taskKey))
    }`,
  });
  const { t: t_common } = useTranslation("translation", {
    keyPrefix: "task.activity.common",
  });
  const user = useRecoilValue(userAtom);
  const patient = useRecoilValue(patientAtom);
  const [taskDataState, setTaskDataState] = useRecoilState(taskDataAtom);
  const setAutoFilledCell = useSetRecoilState(autoFilledCellAtom);

  // 웹 — 이 활동지가 화면에 있는 동안 끝나면 알림 대신 바로 넣는다
  useEffect(() => {
    if (isInappWebview) return;
    setViewing(runKey);
    return () => setViewing((prev) => (prev === runKey ? undefined : prev));
  }, [isInappWebview, runKey, setViewing]);

  // 다른 화면에서 끝났으면 이 활동지를 열 때(내용을 다 불러온 뒤 — 이 버튼은 내용이 있을 때만 그린다) 같은 방식으로 넣는다
  const resolved = useMemo(
    () =>
      phase === "done" && request && runBaseContent
        ? resolveAutoFill({
            content: contentData,
            baseContent: runBaseContent,
            fields: request.fields,
            values,
          })
        : undefined,
    [phase, request, runBaseContent, contentData, values]
  );
  const canApply = !!resolved?.canApply;

  const handleStart = () => {
    const getRequest = () =>
      buildUserSimRequest({
        taskKey,
        title,
        thoughtRecordKey,
        content: contentData,
        t,
        t_common,
        accessToken: user?.accessToken || "",
        pid: patient?.patientId,
        taskDataState,
        setTaskDataState,
      });
    if (isInappWebview) {
      setBaseContent(contentData);
      stream.start(getRequest);
      return;
    }
    // 잠그기 전에 쓰던 칸에서 나온다
    (document.activeElement as HTMLElement)?.blur();
    runs.start({
      taskKey,
      thoughtRecordKey,
      title,
      baseContent: contentData,
      getRequest,
    });
  };

  // 넣었거나 넣지 못해 확인함 — 웹은 진행 표시에서도 지운다
  const finish = () => (isInappWebview ? stream.reset() : runs.remove(runKey));

  const handleApply = () => {
    const result = resolved?.result;
    if (!result) return;
    (document.activeElement as HTMLElement)?.blur();
    const filled = getFilledCells(contentData, result);
    setContentData(result);
    setAutoFilledCell(filled);
    // 처음 채운 블록으로 이동
    const firstBlockIndex = Math.min(
      ...Object.keys(filled).map((key) => Number(key.split("-")[0]))
    );
    if (Number.isFinite(firstBlockIndex)) setMoveToIndex?.(firstBlockIndex);
    finish();
    if (!isInappWebview) {
      const count = Object.keys(filled).length;
      runs.pushToast({
        runKey,
        title,
        kind: "filled",
        // 이미 다 쓴 활동지를 다시 채운 경우
        message: count > 0 ? `${count}칸을 채웠어요` : "채울 빈칸이 없었어요",
      });
    }
  };

  // 다 쓰면 바로 채운다. 채울 수 없을 때만 확인 모달로 이유를 보여준다
  useEffect(() => {
    if (phase === "done" && canApply) handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, canApply]);

  const isRunning = phase === "running" || phase === "canceling";

  return (
    <>
      {/* 중지는 오른쪽 아래 진행 표시에서 */}
      {!isInappWebview && isRunning && globalRun ? (
        <AutoFillRunningButton
          startedAt={globalRun.startedAt}
          onClick={() => setDockOpen(true)}
        />
      ) : (
        <Button
          size="sm"
          variant="Outlined"
          disabled={phase !== "idle"}
          onClick={handleStart}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          자동 채우기
        </Button>
      )}
      {isInappWebview && (
        <AutoFillProgress
          open={isRunning}
          isStopping={phase === "canceling"}
          onStop={stream.cancel}
          steps={stream.steps}
          fields={request?.fields || []}
          values={values}
          fieldStatus={stream.fieldStatus}
          rewriteReasons={stream.rewriteReasons}
          currentKey={stream.currentKey}
        />
      )}
      {/* 확인이 필요한 경우(실패·적용 불가)만 확인 모달 */}
      <CustomModal
        open={phase === "error" || (phase === "done" && !canApply)}
        onClose={() => {}}
        title={"자동 채우기를 하지 못했어요"}
        subtitle={
          phase === "error"
            ? error
            : resolved?.isStructureChanged
              ? "작성 중에 활동지 내용이 바뀌었어요. 다시 시도해 주세요"
              : (resolved?.invalidCount ?? 0) > 0
                ? `형식이 맞지 않는 값이 ${resolved?.invalidCount}개 있어 적용할 수 없어요`
                : "채우지 못한 칸이 있어 적용할 수 없어요"
        }
        actions={[{ label: "확인", onClick: finish, variant: "Solid" }]}
      />
    </>
  );
}
